import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Copy, X, CheckCircle2 } from "lucide-react";
import { trackInitiateCheckout, trackPurchase } from "@/lib/analytics";

/**
 * Modal pay-rail chooser triggered from any service tier.
 * Step 1: rail picker (Visa/MC/Amex via Stripe — or — Bitcoin via Blockonomics)
 * Step 2a: redirect to Stripe Checkout
 * Step 2b: in-page Bitcoin invoice (QR + auto-poll for confirmation)
 * Step 3: receipt on confirmed payment
 */
export default function PayRailModal({ service, leadId = null, onClose }) {
  const [stage, setStage] = useState("choose"); // choose | btc_loading | btc | btc_paid
  const [btcOrder, setBtcOrder] = useState(null);
  const [poll, setPoll] = useState({ status: "pending", confirmations: 0 });
  const purchaseFired = useRef(false);

  // Lock scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Poll BTC status
  useEffect(() => {
    if (stage !== "btc" || !btcOrder?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/payments/btc/status/${btcOrder.id}`);
        if (cancelled) return;
        setPoll({ status: r.data.payment_status, confirmations: r.data.confirmations || 0 });
        if (r.data.payment_status === "paid" && !purchaseFired.current) {
          purchaseFired.current = true;
          trackPurchase({
            transactionId: btcOrder.id,
            value: btcOrder.amount_usd,
            currency: "USD",
            serviceType: service.id,
            serviceName: service.name,
          });
          setStage("btc_paid");
        }
      } catch { /* ignore */ }
    };
    const iv = setInterval(tick, 8000);
    tick();
    return () => { cancelled = true; clearInterval(iv); };
  }, [stage, btcOrder, service]);

  const payStripe = async () => {
    try {
      trackInitiateCheckout({ rail: "stripe", serviceType: service.id });
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        service_type: service.id,
        origin_url: window.location.origin,
        lead_id: leadId,
      });
      if (data.url) window.location.href = data.url;
      else toast.error("Stripe session failed to initialise.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed to initialise.");
    }
  };

  const payBTC = async () => {
    setStage("btc_loading");
    try {
      trackInitiateCheckout({ rail: "btc", serviceType: service.id });
      const { data } = await axios.post(`${API}/payments/btc/create`, {
        service_type: service.id,
        lead_id: leadId,
      });
      setBtcOrder(data);
      setStage("btc");
    } catch (e) {
      toast.error(e.response?.data?.detail || "BTC checkout failed.");
      setStage("choose");
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied.");
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8" data-testid="pay-rail-modal">
      <button onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-sm" aria-label="Close" />
      <div className="relative w-full max-w-2xl bg-paper text-paperink border border-paperline shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-paperline">
          <div>
            <div className="mono-label-paper">{service.index ? `TIER ${service.index} · ` : ""}RETAINER GATEWAY</div>
            <div className="font-serif-display text-2xl mt-1">{service.name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-paperhover transition-colors" data-testid="payrail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {stage === "choose" && (
          <div className="p-8">
            <div className="font-serif-display text-3xl leading-tight">How do you want to pay?</div>
            <p className="text-paperink/70 mt-3 text-sm">
              Both rails produce identical deliverables. Bitcoin is preferred for matters where
              financial-rail privacy is a concern.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-paperline border border-paperline">
              <button onClick={payStripe} className="bg-paper p-8 text-left hover:bg-paperhover transition-colors" data-testid="payrail-stripe">
                <div className="mono-label-paper">RAIL A</div>
                <div className="font-serif-display text-2xl mt-3">Visa / MC / Amex</div>
                <div className="text-paperink/70 text-sm mt-3">Instant card / ACH via Stripe. Receipt emailed.</div>
                <div className="mono-label-paper text-paperink mt-8">PROCEED →</div>
              </button>
              <button onClick={payBTC} className="bg-paper p-8 text-left hover:bg-paperhover transition-colors" data-testid="payrail-btc">
                <div className="mono-label-paper">RAIL B</div>
                <div className="font-serif-display text-2xl mt-3">Bitcoin · BTC</div>
                <div className="text-paperink/70 text-sm mt-3">On-chain payment. Auto-confirmed.</div>
                <div className="mono-label-paper text-paperink mt-8">PROCEED →</div>
              </button>
            </div>

            <div className="mt-8 mono-label-paper text-paperink/50 text-center">
              ${service.amount_display ?? `${service.amount}`} · USD · Fixed Fee
            </div>
          </div>
        )}

        {stage === "btc_loading" && (
          <div className="p-12 text-center">
            <div className="mono-label-paper">ISSUING ADDRESS…</div>
          </div>
        )}

        {stage === "btc" && btcOrder && (
          <div className="p-8">
            {!btcOrder.configured && (
              <div className="border border-amber-700 bg-amber-50 text-amber-900 p-4 mb-6">
                <div className="mono-label-paper" style={{color: "#92400e"}}>BTC RAIL · OPERATOR CONFIG PENDING</div>
                <p className="text-sm mt-2">
                  This order is reserved as a ticket — an operator will contact you to arrange an
                  out-of-band BTC invoice.
                </p>
              </div>
            )}

            {btcOrder.configured && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-paperline border border-paperline">
                  <div className="bg-paper p-6">
                    <div className="mono-label-paper">AMOUNT DUE</div>
                    <div className="font-serif-display text-3xl mt-3">
                      {btcOrder.amount_btc?.toFixed(8)} <span className="text-base">BTC</span>
                    </div>
                    <div className="mono-label-paper mt-2">≈ ${btcOrder.amount_usd?.toFixed(2)} USD</div>
                    {btcOrder.btc_price_usd && (
                      <div className="mono-label-paper text-paperink/50 mt-4">
                        LOCK · ${btcOrder.btc_price_usd.toLocaleString()} / BTC
                      </div>
                    )}
                    <div className="mt-6 border-t border-paperline pt-4">
                      <div className="mono-label-paper">STATUS</div>
                      <div className="font-mono mt-3 flex items-center gap-2 text-sm">
                        <span className="w-1.5 h-1.5 bg-amber-600 rounded-full node-pulse" />
                        {String(poll.status).toUpperCase().replace(/_/g, " ")}
                        {poll.confirmations > 0 && <span className="text-paperink/50"> · {poll.confirmations} CONF</span>}
                      </div>
                    </div>
                  </div>

                  <div className="bg-paper p-6 flex flex-col items-center">
                    <div className="mono-label-paper self-start mb-3">BITCOIN ADDRESS</div>
                    <div className="bg-white border border-paperline p-2">
                      <img
                        alt="BTC QR"
                        className="w-[200px] h-[200px]"
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&qzone=2&data=${encodeURIComponent(`bitcoin:${btcOrder.bitcoin_address}?amount=${btcOrder.amount_btc}`)}`}
                      />
                    </div>
                    <div className="mt-4 w-full font-mono text-xs break-all bg-paperhover border border-paperline p-2 text-center">
                      {btcOrder.bitcoin_address}
                    </div>
                    <button onClick={() => copy(btcOrder.bitcoin_address)} className="btn-paper-secondary mt-3 inline-flex items-center gap-2 !py-2 !px-3" data-testid="btc-copy">
                      <Copy className="w-3.5 h-3.5" /> Copy address
                    </button>
                  </div>
                </div>

                <div className="mt-6 mono-label-paper text-paperink/50 text-center">
                  EXPIRES · {btcOrder.expires_at ? new Date(btcOrder.expires_at).toLocaleTimeString() : "—"} · ORDER {btcOrder.id}
                </div>
              </>
            )}

            <div className="mt-6 flex justify-between items-center">
              <button onClick={() => setStage("choose")} className="mono-label-paper hover:text-paperink" data-testid="btc-back">
                ← CHANGE RAIL
              </button>
            </div>
          </div>
        )}

        {stage === "btc_paid" && btcOrder && (
          <div className="p-12 text-center" data-testid="btc-paid">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
            <div className="font-serif-display text-3xl mt-6">Retainer captured.</div>
            <p className="text-paperink/70 mt-4 max-w-md mx-auto">
              Your {service.name} is now retained. A privileged memorandum will be issued to the email on file.
            </p>
            <div className="mt-8 inline-block text-left border border-paperline p-6">
              <Stat label="AMOUNT" value={`${btcOrder.amount_btc?.toFixed(8)} BTC ≈ $${btcOrder.amount_usd?.toFixed(2)} USD`} />
              <Stat label="ORDER" value={btcOrder.id} mono />
              <Stat label="STATUS" value="PAID · CONFIRMED" />
            </div>
            <div className="mt-8">
              <button onClick={onClose} className="btn-paper-primary" data-testid="btc-paid-close">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="py-2">
      <div className="mono-label-paper">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono text-xs break-all" : "font-serif-display text-lg"}`}>{value}</div>
    </div>
  );
}
